/**
 * Local clock uses a verified IANA zone, never a raw UTC offset: daylight saving
 * is handled by the device. Multi-zone countries require an unambiguous city.
 */
const COUNTRY_ZONES: Record<string, string> = {
  AE:"Asia/Dubai", AT:"Europe/Vienna", BE:"Europe/Brussels",
  BG:"Europe/Sofia", CH:"Europe/Zurich", CN:"Asia/Shanghai", CO:"America/Bogota",
  CR:"America/Costa_Rica", CU:"America/Havana", CV:"Atlantic/Cape_Verde", CY:"Asia/Nicosia",
  CZ:"Europe/Prague", DE:"Europe/Berlin", DK:"Europe/Copenhagen", DO:"America/Santo_Domingo",
  EG:"Africa/Cairo", EE:"Europe/Tallinn", ES:"Europe/Madrid", ET:"Africa/Addis_Ababa",
  FI:"Europe/Helsinki", FR:"Europe/Paris", GB:"Europe/London", GH:"Africa/Accra",
  GR:"Europe/Athens", HR:"Europe/Zagreb", HU:"Europe/Budapest", HK:"Asia/Hong_Kong",
  IE:"Europe/Dublin", IL:"Asia/Jerusalem", IN:"Asia/Kolkata", IS:"Atlantic/Reykjavik",
  IT:"Europe/Rome", JP:"Asia/Tokyo", KE:"Africa/Nairobi", KR:"Asia/Seoul",
  LK:"Asia/Colombo", LT:"Europe/Vilnius", LV:"Europe/Riga", MA:"Africa/Casablanca",
  MY:"Asia/Kuala_Lumpur", NL:"Europe/Amsterdam", NO:"Europe/Oslo", NP:"Asia/Kathmandu",
  PE:"America/Lima", PH:"Asia/Manila", PL:"Europe/Warsaw", PT:"Europe/Lisbon",
  QA:"Asia/Qatar", RO:"Europe/Bucharest", SA:"Asia/Riyadh", SE:"Europe/Stockholm",
  SG:"Asia/Singapore", SI:"Europe/Ljubljana", SK:"Europe/Bratislava", TH:"Asia/Bangkok",
  TN:"Africa/Tunis", TR:"Europe/Istanbul", TW:"Asia/Taipei", TZ:"Africa/Dar_es_Salaam",
  UA:"Europe/Kyiv", UY:"America/Montevideo", VN:"Asia/Ho_Chi_Minh", ZA:"Africa/Johannesburg"
};
const CITY_ZONES: Record<string, Array<[RegExp,string]>> = {
  US: [
    [/phoenix/i,"America/Phoenix"],
    [/los angeles|san francisco|seattle|san diego|las vegas|portland|california/i,"America/Los_Angeles"],
    [/denver|salt lake|colorado/i,"America/Denver"],
    [/chicago|dallas|houston|austin|new orleans/i,"America/Chicago"],
    [/new york|boston|washington|miami|orlando|philadelphia|atlanta/i,"America/New_York"],
    [/anchorage|alaska/i,"America/Anchorage"], [/honolulu|hawaii/i,"Pacific/Honolulu"]
  ],
  CA: [
    [/vancouver|victoria/i,"America/Vancouver"], [/calgary|edmonton/i,"America/Edmonton"],
    [/winnipeg/i,"America/Winnipeg"], [/toronto|ottawa|montreal|québec|quebec/i,"America/Toronto"],
    [/halifax/i,"America/Halifax"], [/st\.?\s*john(?:[\u0027\u2019]s|s)\b|newfoundland/i,"America/St_Johns"]
  ],
  AU: [
    [/eucla/i,"Australia/Eucla"], [/lord howe/i,"Australia/Lord_Howe"],
    [/sydney|canberra|melbourne/i,"Australia/Sydney"], [/brisbane|gold coast/i,"Australia/Brisbane"],
    [/adelaide/i,"Australia/Adelaide"], [/darwin/i,"Australia/Darwin"], [/perth/i,"Australia/Perth"],
    [/hobart|tasmania/i,"Australia/Hobart"]
  ],
  BR: [ [/são paulo|sao paulo|rio de janeiro|bras[ií]lia/i,"America/Sao_Paulo"], [/manaus/i,"America/Manaus"], [/recife/i,"America/Recife"] ],
  ID: [ [/jakarta|bandung/i,"Asia/Jakarta"], [/bali|denpasar|makassar/i,"Asia/Makassar"], [/papua|jayapura|sorong/i,"Asia/Jayapura"] ],
  MX: [ [/mexico city|ciudad de m[eé]xico|cancún|cancun/i,"America/Mexico_City"], [/tijuana/i,"America/Tijuana"], [/monterrey/i,"America/Monterrey"] ],
  AR: [ [/buenos aires/i,"America/Argentina/Buenos_Aires"], [/mendoza/i,"America/Argentina/Mendoza"] ],
  CL: [ [/hanga roa/i,"Pacific/Easter"], [/puerto natales/i,"America/Punta_Arenas"], [/santiago de chile|santiago/i,"America/Santiago"] ],
  GL: [ [/nuuk/i,"America/Nuuk"] ],
  KI: [ [/kiritimati/i,"Pacific/Kiritimati"] ],
  PS: [ [/gaza/i,"Asia/Gaza"] ],
  RU: [ [/moscow|mosca|san pietroburgo|saint petersburg/i,"Europe/Moscow"], [/vladivostok/i,"Asia/Vladivostok"] ],
};

export function resolveDestinationTimeZone(countryCode: string, destination: string): string | null {
  const code = countryCode.trim().toUpperCase();
  if (code === "ES" && /canari|tenerife|lanzarote|gran canaria/i.test(destination)) return "Atlantic/Canary";
  if (code === "PT" && /azores|a[cç]ores|ponta delgada/i.test(destination)) return "Atlantic/Azores";
  if (code === "PT" && /madeira|funchal/i.test(destination)) return "Atlantic/Madeira";
  if (code === "NZ" && /chatham/i.test(destination)) return "Pacific/Chatham";
  const known = CITY_ZONES[code]?.find(([pattern]) => pattern.test(destination));
  if (known) return known[1];
  if (CITY_ZONES[code]) return null;
  if (code === "NZ") return "Pacific/Auckland";
  return COUNTRY_ZONES[code] || null;
}

function offsetMinutes(zone: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone, year:"numeric", month:"2-digit", day:"2-digit",
    hour:"2-digit", minute:"2-digit", second:"2-digit", hourCycle:"h23"
  }).formatToParts(instant);
  const fields = Object.fromEntries(parts.map((part) => [part.type, Number(part.value)]));
  const wall = Date.UTC(fields.year, fields.month - 1, fields.day, fields.hour, fields.minute, fields.second);
  return Math.round((wall - instant.getTime()) / 60000);
}

export function formatDestinationTime(zone: string, instant: Date, language: string, fromHome: string): { localTime:string; offsetText:string; delta:string; text:string } | null {
  if (!zone) return null;
  try {
    const locale = language === "es" ? "es-ES" : language === "en" ? "en-GB" : "it-IT";
    const localTime = new Intl.DateTimeFormat(locale,{timeZone:zone,hour:"2-digit",minute:"2-digit",hourCycle:"h23"}).format(instant);
    const homeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const deltaMinutes = offsetMinutes(zone, instant) - offsetMinutes(homeZone, instant);
    const sign = deltaMinutes < 0 ? "−" : deltaMinutes > 0 ? "+" : "±";
    const minutes = Math.abs(deltaMinutes);
    const delta = `${sign}${Math.floor(minutes/60)}${minutes%60 ? `:${String(minutes%60).padStart(2,"0")}` : ""} h`;
    const offsetText = `${delta} · ${fromHome}`;
    return { localTime, offsetText, delta, text:`${localTime} · ${offsetText}` };
  } catch { return null; }
}
